import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  Link,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ReplayIcon from '@mui/icons-material/Replay';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';

import { EmptyStateMessage } from '@/components/common';
import { formatDate } from '@/utils/dateFormatter';
import type { ManualJobEndAssignment } from '@/api/types/manualJobEnd.types';

type SortField =
  | 'name'
  | 'assignment'
  | 'project'
  | 'projectCode'
  | 'tenure'
  | 'collator'
  | 'startDate'
  | 'endDate'
  | 'jeGenerated';
type SortDir = 'asc' | 'desc';
type GeneratedFilter = '' | 'generated' | 'notGenerated';

// Filter-row styling mirrors the Active Job Ends table (JobEndAdminPage) filter row.
const filterCellSx = { bgcolor: 'white', py: 1, borderBottom: 1, borderColor: 'divider' };
const filterFieldSx = {
  width: '100%',
  bgcolor: 'white',
  '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'divider' } },
};

// Shared width so the Generate (contained) and Reopen (outlined) row actions render the same size.
const actionButtonSx = { minWidth: 120 };

// Under `tableLayout: 'fixed'` the widths below are taken from the header row alone and never from
// the body, which is what keeps the columns identical whether rows are showing or a filter has
// emptied them. Project Name deliberately carries no width: a fixed layout hands the leftover space
// to the column that has none, so it is the one that flexes and keeps the table fluid.
//
// These two hold a fixed-size control, so they are sized to that control and not to the table: a
// percentage grows with the window and leaves the control swimming in space. rem tracks the font
// rather than the container, so each column hugs its content at any window width. Neither forces the
// table wider than its container - only an explicit minWidth on the Table does that, which is what
// used to make the container scroll sideways.
//
// 10rem = the 120px row button plus the cell's 32px padding.
const actionColumnWidth = '10rem';
// 11rem = "Not generated" (~92px) plus the Select's padding and dropdown arrow, plus cell padding.
const jeGeneratedColumnWidth = '11rem';

interface AssignmentsTableProps {
  assignments: ManualJobEndAssignment[];
  isLoading: boolean;
  onGenerate: (assignment: ManualJobEndAssignment) => void;
  onReopen: (assignment: ManualJobEndAssignment) => void;
  pendingAssignmentId?: string | null;
}

const appraiseeName = (assignment: ManualJobEndAssignment) =>
  `${assignment.appraiseeFirstName} ${assignment.appraiseeLastName}`;

function sortValue(assignment: ManualJobEndAssignment, field: SortField): string {
  switch (field) {
    case 'name':
      return appraiseeName(assignment);
    case 'assignment':
      return assignment.assignmentId;
    case 'project':
      return assignment.projectName;
    case 'projectCode':
      return assignment.projectCode;
    case 'tenure':
      return assignment.appraiseeTenureInGrade;
    case 'collator':
      return assignment.collatorName ?? '';
    case 'startDate':
      return assignment.assignmentStartDate;
    case 'endDate':
      return assignment.assignmentEndDate ?? '';
    case 'jeGenerated':
      return assignment.jobEndGenerated ? '1' : '0';
  }
}

const columns: { field: SortField; label: string; width?: string }[] = [
  { field: 'name', label: 'Name', width: '11%' },
  { field: 'assignment', label: 'Assignment', width: '10%' },
  { field: 'project', label: 'Project Name' },
  { field: 'projectCode', label: 'Project Code', width: '9%' },
  { field: 'tenure', label: 'Tenure', width: '7%' },
  { field: 'collator', label: 'Collator', width: '9%' },
  { field: 'startDate', label: 'Start Date', width: '8%' },
  { field: 'endDate', label: 'End Date', width: '8%' },
  { field: 'jeGenerated', label: 'JE Status', width: jeGeneratedColumnWidth },
];

export function AssignmentsTable({
  assignments,
  isLoading,
  onGenerate,
  onReopen,
  pendingAssignmentId,
}: AssignmentsTableProps) {
  const [sortField, setSortField] = useState<SortField>('project');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const [projectFilter, setProjectFilter] = useState('');
  const [codeFilter, setCodeFilter] = useState('');
  const [generatedFilter, setGeneratedFilter] = useState<GeneratedFilter>('');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const activeFilterCount =
    (projectFilter.trim() ? 1 : 0) +
    (codeFilter.trim() ? 1 : 0) +
    (generatedFilter !== '' ? 1 : 0);

  const handleClearFilters = () => {
    setProjectFilter('');
    setCodeFilter('');
    setGeneratedFilter('');
  };

  const visibleAssignments = useMemo(() => {
    const project = projectFilter.trim().toLowerCase();
    const code = codeFilter.trim().toLowerCase();

    const filtered = assignments.filter((assignment) => {
      if (project && !assignment.projectName.toLowerCase().includes(project)) {
        return false;
      }
      if (code && !assignment.projectCode.toLowerCase().includes(code)) {
        return false;
      }
      if (generatedFilter === 'generated' && !assignment.jobEndGenerated) {
        return false;
      }
      if (generatedFilter === 'notGenerated' && assignment.jobEndGenerated) {
        return false;
      }
      return true;
    });

    return [...filtered].sort((a, b) => {
      const cmp = sortValue(a, sortField).localeCompare(sortValue(b, sortField));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [assignments, projectFilter, codeFilter, generatedFilter, sortField, sortDir]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (assignments.length === 0) {
    return (
      <Box sx={{ mt: 3 }}>
        <EmptyStateMessage message="No assignments found for the selected person." />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <TableContainer>
        <Table
          size="small"
          sx={{
            tableLayout: 'fixed',
            // Proportional columns cannot widen for a long value, so wrap it instead of overflowing.
            '& td': { wordBreak: 'break-word' },
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.field} sx={{ width: column.width }}>
                  <TableSortLabel
                    active={sortField === column.field}
                    direction={sortField === column.field ? sortDir : 'asc'}
                    onClick={() => handleSort(column.field)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="center" sx={{ width: actionColumnWidth }}>
                Action
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={filterCellSx} />
              <TableCell sx={filterCellSx} />
              <TableCell sx={filterCellSx}>
                <TextField
                  size="small"
                  placeholder="Type..."
                  value={projectFilter}
                  onChange={(e) => setProjectFilter(e.target.value)}
                  slotProps={{ htmlInput: { 'aria-label': 'Filter by project name' } }}
                  InputProps={{
                    endAdornment: projectFilter ? (
                      <IconButton
                        size="small"
                        onClick={() => setProjectFilter('')}
                        aria-label="Clear project filter"
                        sx={{ p: 0.5 }}
                      >
                        <ClearIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    ) : null,
                  }}
                  sx={filterFieldSx}
                />
              </TableCell>
              <TableCell sx={filterCellSx}>
                <TextField
                  size="small"
                  placeholder="Type..."
                  value={codeFilter}
                  onChange={(e) => setCodeFilter(e.target.value)}
                  slotProps={{ htmlInput: { 'aria-label': 'Filter by project code' } }}
                  InputProps={{
                    endAdornment: codeFilter ? (
                      <IconButton
                        size="small"
                        onClick={() => setCodeFilter('')}
                        aria-label="Clear project code filter"
                        sx={{ p: 0.5 }}
                      >
                        <ClearIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    ) : null,
                  }}
                  sx={filterFieldSx}
                />
              </TableCell>
              <TableCell sx={filterCellSx} />
              <TableCell sx={filterCellSx} />
              <TableCell sx={filterCellSx} />
              <TableCell sx={filterCellSx} />
              <TableCell sx={filterCellSx}>
                <FormControl size="small" fullWidth>
                  <Select
                    displayEmpty
                    value={generatedFilter}
                    onChange={(e: SelectChangeEvent) =>
                      setGeneratedFilter(e.target.value as GeneratedFilter)
                    }
                    slotProps={{ input: { 'aria-label': 'Filter by JE status' } }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <Typography
                            component="span"
                            sx={{ color: 'text.secondary', textTransform: 'none' }}
                          >
                            Type...
                          </Typography>
                        );
                      }
                      return selected === 'generated' ? 'Generated' : 'Not generated';
                    }}
                    endAdornment={
                      generatedFilter ? (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setGeneratedFilter('');
                          }}
                          aria-label="Clear JE status filter"
                          sx={{ p: 0.5, position: 'absolute', right: 4 }}
                        >
                          <ClearIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      ) : null
                    }
                    sx={{
                      bgcolor: 'white',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                      // Theme's `typography.button` uppercases the Select display; keep it as authored
                      // so the placeholder and values match the plain-text filters. Clamping the
                      // display keeps the longest value from widening the pinned column.
                      '& .MuiSelect-select': {
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                      // With a filter active the clear button sits in the arrow's slot instead of
                      // alongside it, so the control needs no extra width for it.
                      ...(generatedFilter && { '& .MuiSelect-icon': { display: 'none' } }),
                    }}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    <MenuItem value="generated">Generated</MenuItem>
                    <MenuItem value="notGenerated">Not generated</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell align="center" sx={filterCellSx}>
                {activeFilterCount > 1 && (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleClearFilters}
                    aria-label="Clear all filters"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: 'error.main',
                      textDecoration: 'none',
                      whiteSpace: 'nowrap',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Clear all <CloseIcon sx={{ fontSize: 16 }} />
                  </Link>
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleAssignments.map((assignment) => {
              const isPending = pendingAssignmentId === assignment.assignmentId;
              return (
                <TableRow key={assignment.assignmentId} hover>
                  <TableCell>{appraiseeName(assignment)}</TableCell>
                  <TableCell>{assignment.assignmentId}</TableCell>
                  <TableCell>{assignment.projectName}</TableCell>
                  <TableCell>{assignment.projectCode}</TableCell>
                  <TableCell>{assignment.appraiseeTenureInGrade}</TableCell>
                  <TableCell>{assignment.collatorName ?? '-'}</TableCell>
                  <TableCell>{formatDate(assignment.assignmentStartDate)}</TableCell>
                  <TableCell>
                    {assignment.assignmentEndDate
                      ? formatDate(assignment.assignmentEndDate)
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={assignment.jobEndGenerated ? 'Yes' : 'No'}
                      size="small"
                      color={assignment.jobEndGenerated ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {assignment.jobEndGenerated ? (
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        startIcon={
                          isPending ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <ReplayIcon />
                          )
                        }
                        onClick={() => onReopen(assignment)}
                        disabled={isPending}
                        aria-busy={isPending}
                        aria-label={`Reopen Job End for ${assignment.projectName}`}
                        sx={actionButtonSx}
                      >
                        Reopen
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        startIcon={
                          isPending ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <AddIcon />
                          )
                        }
                        onClick={() => onGenerate(assignment)}
                        disabled={isPending}
                        aria-busy={isPending}
                        aria-label={`Generate Job End for ${assignment.projectName}`}
                        sx={actionButtonSx}
                      >
                        Generate
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {visibleAssignments.length === 0 && (
              // Spanning every column centres the message across the table. The columns are laid out
              // from the header row, so this cell cannot influence their widths.
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No assignments match the current filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default AssignmentsTable;
